import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'wouter';
import { ArrowLeft, CircleAlert, Inbox, Loader2, Send, Activity as ActivityIcon } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import {
  useAdminOpsOverview,
  useConductorThread,
  useSendConductorMessage,
} from '../api/useAdminOps';
import Markdown from '@/components/Markdown';
import { RefreshButton, eventLabel, timeAgo } from '../components/ops/opsUi';

// The ops console: one place to ask the conductor about an event, with the
// state you'd be asking about beside it.
//
// The conductor is a role, not a process — each turn is a `conductor.reply`
// agent run against the durable ledger, so it answers when nothing is
// scheduled, survives a deploy mid-answer, and every exchange is a row. That
// is also why the reply arrives by polling rather than in the send response.
//
// Read-only in this phase: it explains what is happening, it changes nothing.

const SUGGESTIONS = [
  'How is this event looking overall?',
  'Which neighborhoods are behind, and why?',
  'Is anything stuck rather than just in progress?',
  'What still needs a reservation?',
];

const CARD = 'bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700';
const LINK_BTN =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800';

export default function OpsConductor() {
  const { data: overviewRes } = useAdminOpsOverview();
  const overview = overviewRes?.data;
  const [eventTimeId, setEventTimeId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Tiles are per (event × city); the conductor works at the event level, so
  // collapse to unique event ids. Overview order is already soonest-first.
  const events = useMemo(() => {
    const seen = new Map<string, { id: string; label: string }>();
    for (const tile of overview?.tiles ?? []) {
      if (seen.has(tile.eventTimeId)) continue;
      seen.set(tile.eventTimeId, { id: tile.eventTimeId, label: eventLabel(tile.eventTimeId) });
    }
    return [...seen.values()];
  }, [overview]);

  useEffect(() => {
    if (!eventTimeId && events.length > 0) setEventTimeId(events[0].id);
  }, [events, eventTimeId]);

  const { data: thread, isLoading, refetch, isFetching } = useConductorThread(eventTimeId);
  const send = useSendConductorMessage();

  const turns = thread?.turns ?? [];
  const pending = thread?.pending ?? false;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns.length, pending]);

  const submit = (text: string) => {
    const content = text.trim();
    if (!content || !eventTimeId || send.isPending || pending) return;
    setDraft('');
    send.mutate({ eventTimeId, content });
  };

  const tilesForEvent = (overview?.tiles ?? []).filter((t) => t.eventTimeId === eventTimeId);
  const sum = (pick: (t: AdminAPI.OpsTile) => number) =>
    tilesForEvent.reduce((n, t) => n + pick(t), 0);
  const pendingDecisions = sum((t) => t.pendingDecisions);
  const unassigned = sum((t) => t.unassigned);
  const unreserved = sum((t) => t.reservationsNeeded);
  const neighborhoodCount = sum((t) => t.neighborhoods.length);
  const worstHealth = tilesForEvent.find((t) => t.health === 'red')
    ? 'red'
    : tilesForEvent.find((t) => t.health === 'yellow')
      ? 'yellow'
      : 'green';

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Link href="/ops" className={LINK_BTN}>
          <ArrowLeft className="w-4 h-4" />
          Ops
        </Link>
        <h1 className="text-xl font-semibold">Conductor</h1>
        <span className="px-2 py-0.5 rounded-full border border-neutral-300 dark:border-neutral-600 text-[11px] font-medium text-dark-muted">
          read-only
        </span>
        <div className="ml-auto flex items-center gap-2">
          <select
            className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-sm"
            value={eventTimeId ?? ''}
            onChange={(e) => setEventTimeId(e.target.value || null)}
            aria-label="Event"
          >
            {events.length === 0 && <option value="">No active events</option>}
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {e.label}
              </option>
            ))}
          </select>
          <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div className={`${CARD} flex flex-col h-[calc(100vh-200px)] min-h-[420px]`}>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-dark-muted">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading the thread…
              </div>
            )}

            {!isLoading && turns.length === 0 && (
              <div className="m-auto text-center max-w-md flex flex-col gap-3">
                <p className="text-sm text-dark-muted">
                  Ask about this event. The conductor answers from the run ledger, the open
                  problems the guardian is tracking, and what earlier runs learned about your
                  venues.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="px-2.5 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
                      onClick={() => submit(s)}
                      disabled={!eventTimeId}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {turns.map((turn) => (
              <ConductorMessage key={turn.id} turn={turn} />
            ))}

            {pending && (
              <div className="flex items-center gap-2 text-sm text-dark-muted">
                <Loader2 className="w-4 h-4 animate-spin" />
                Reading the ledger…
              </div>
            )}
          </div>

          <form
            className="border-t border-neutral-200 dark:border-neutral-700 p-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              submit(draft);
            }}
          >
            <input
              className="flex-1 px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-sm"
              placeholder={eventTimeId ? 'Ask about this event…' : 'Pick an event first'}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={!eventTimeId || pending}
              aria-label="Message"
            />
            <button
              type="submit"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              disabled={!draft.trim() || !eventTimeId || pending || send.isPending}
            >
              {send.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send
            </button>
          </form>
        </div>

        {/* The state you'd otherwise be asking about, already on screen. */}
        <aside className="flex flex-col gap-3">
          <div className={`${CARD} p-4`}>
            <h2 className="text-xs uppercase tracking-wide text-dark-muted mb-3 flex items-center gap-2">
              Right now
              <span
                className={`w-2 h-2 rounded-full ${
                  worstHealth === 'red'
                    ? 'bg-red-500'
                    : worstHealth === 'yellow'
                      ? 'bg-amber-500'
                      : 'bg-emerald-500'
                }`}
                aria-label={`health: ${worstHealth}`}
              />
            </h2>
            <dl className="flex flex-col gap-2 text-sm">
              <StatRow label="Cities" value={tilesForEvent.length} />
              <StatRow label="Neighborhoods" value={neighborhoodCount} />
              <StatRow label="Unassigned guests" value={unassigned} warn={unassigned > 0} />
              <StatRow label="Tables needing a booking" value={unreserved} warn={unreserved > 0} />
              <StatRow
                label="Pending decisions"
                value={pendingDecisions}
                warn={pendingDecisions > 0}
              />
            </dl>
          </div>

          <div className={`${CARD} p-4 flex flex-col gap-2`}>
            <h2 className="text-xs uppercase tracking-wide text-dark-muted">Go deeper</h2>
            <Link href="/ops/decisions" className={LINK_BTN}>
              <Inbox className="w-4 h-4" />
              Decision inbox
            </Link>
            <Link href="/ops/activity" className={LINK_BTN}>
              <ActivityIcon className="w-4 h-4" />
              Automation activity
            </Link>
          </div>

          <p className="text-xs text-dark-muted px-1">
            The conductor reads; it does not act. Anything it says should change still goes through
            a run or a decision card.
          </p>
        </aside>
      </div>
    </div>
  );
}

function StatRow({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-dark-muted">{label}</dt>
      <dd className={`font-mono tabular-nums ${warn ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

function ConductorMessage({ turn }: { turn: AdminAPI.ConductorTurn }) {
  const isUser = turn.role === 'user';
  const failed = turn.runStatus === 'failed';
  return (
    <div className={`flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
      <div
        className={[
          'max-w-[85%] rounded-lg px-3 py-2 text-sm',
          isUser
            ? 'bg-emerald-600 text-white'
            : failed
              ? 'border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40'
              : 'bg-neutral-100 dark:bg-neutral-800',
        ].join(' ')}
      >
        {isUser ? (
          <span className="whitespace-pre-wrap">{turn.content}</span>
        ) : (
          <Markdown>{turn.content}</Markdown>
        )}
      </div>
      <span className="text-[11px] text-dark-muted px-1 flex items-center gap-1">
        {failed && <CircleAlert className="w-3 h-3 text-red-500" />}
        {timeAgo(turn.createdAt)}
      </span>
    </div>
  );
}
